import { useState } from "react";
import { homeData } from "../../../mocks/homeData";
import type { TestimonialItem } from "../../../api";

interface Props {
  data?: { title?: string; rating?: string; items?: TestimonialItem[] } | null;
}

export default function TestimonialsSection({ data: apiData }: Props) {
  // API 可能返回空数组或无 items 字段，此时回退到 mock 数据
  const testimonials = (apiData && apiData.items && apiData.items.length > 0) ? apiData : homeData.testimonials;
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.items.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.items.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="bg-light-blue py-16 md:py-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wider uppercase mb-3 block">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-dark">
              What Clients Say
            </h2>
          </div>
          <div className="flex items-center gap-2 mt-6 md:mt-0">
            <button
              onClick={handlePrev}
              className="w-12 h-12 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-white hover:border-primary transition-colors"
              aria-label="Previous testimonial"
            >
              <i className="ri-arrow-left-line text-dark"></i>
            </button>
            <button
              onClick={handleNext}
              className="w-12 h-12 bg-dark rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors"
              aria-label="Next testimonial"
            >
              <i className="ri-arrow-right-line text-white"></i>
            </button>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.items.map((item, index) => {
            const isActive = index === currentIndex;
            return (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 md:p-8 transition-all duration-500 cursor-pointer ${
                  isActive
                    ? "ring-2 ring-primary shadow-lg scale-100"
                    : "ring-1 ring-gray-200 hover:ring-primary/50 hover:shadow-md scale-[0.98]"
                }`}
                onClick={() => setCurrentIndex(index)}
              >
                {/* Quote Icon */}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                  <i className="ri-double-quotes-l text-primary text-lg"></i>
                </div>

                {/* Quote */}
                <p className="text-gray-700 text-sm leading-relaxed mb-6">
                  "{item.quote}"
                </p>

                {/* User Info */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-dark font-semibold text-sm">{item.name}</p>
                    <p className="text-gray-500 text-xs">{item.role}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Stats Bar */}
        <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-12">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <i key={i} className="ri-star-fill text-secondary text-sm"></i>
              ))}
            </div>
            <span className="text-dark font-bold text-lg">{testimonials.rating}</span>
            <span className="text-gray-500 text-sm">out of 5</span>
          </div>
          <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <span className="text-dark font-bold text-lg">500+</span>
            <span className="text-gray-500 text-sm">Happy Clients</span>
          </div>
        </div>
      </div>
    </section>
  );
}